import React from 'react';

import styled from '@emotion/styled';
import { Alert } from '@lightspeed/flame/Alert';
import { Spinner } from '@lightspeed/flame/Spinner';

import withLocalizeAwareIframe from 'pages/utils/withLocalizeAwareIframe';
import withRouteAwareIframe from 'pages/utils/withRouteAwareIframe';
import withValidSessionIframe from 'pages/utils/withValidSessionIframe';
import Monitoring from 'root/apps/shared_modules/Monitoring';

interface ApiResponse {
  magicLink: string;
  iframe: boolean;
}

interface ApiError {
  detail: string;
}

interface Props {}

interface State {
  errorMsg: string;
  isLoading: boolean;
  magicLink: string;
  shouldRenderIframe: boolean;
}

const SpinnerWrapper = styled('div')`
  align-content: space-between;
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-around;
  width: 100%;
`;

const StyledIframe = styled('iframe')<{ hidden?: boolean }>`
  border: none;
  width: ${props => (props.hidden ? 0 : '100%')};
  height: ${props => (props.hidden ? 0 : '100%')};
  display: ${props => (props.hidden ? 'hidden' : 'block')};
`;

class AccountingPosa extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      errorMsg: '',
      isLoading: true,
      magicLink: '',
      shouldRenderIframe: true,
    };
  }

  componentDidMount() {
    window.merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            text: 'Accounting',
            name: 'accounting',
          },
        ],
      },
    });

    this.fetchMagicLink();
  }

  fetchMagicLink = async () => {
    try {
      const apiResponse: ApiResponse = await window.lsCloud.getJSONFromAPI(
        '/admin/accounting/magiclink'
      );
      const { magicLink, iframe } = apiResponse;

      // Force type-check on the response
      if (typeof magicLink === 'string' && typeof iframe === 'boolean') {
        this.setState({ magicLink, shouldRenderIframe: iframe });
      } else {
        this.setState({ errorMsg: 'The response from the server is invalid.' });
      }
    } catch (error) {
      const errors: ApiError[] = error?.responseJSON?.errors || [];
      const firstError = errors[0];
      const errorMsg = firstError?.detail || 'Could not fetch the accounting link.';

      this.setState({ errorMsg });

      Monitoring.NRError(error, 'AccountingPosa', { errors });
    }

    this.setState({ isLoading: false });
  };

  render() {
    const { isLoading, errorMsg, magicLink, shouldRenderIframe } = this.state;

    if (isLoading) {
      return (
        <>
          <SpinnerWrapper>
            <Spinner size="2rem" />
          </SpinnerWrapper>
          <StyledIframe title="Accounting" hidden={true} />
        </>
      );
    }

    if (errorMsg) {
      return (
        <Alert type="danger" noCloseBtn margin={2}>
          {errorMsg}
        </Alert>
      );
    }

    // Redirects the parent window for authentication
    if (!shouldRenderIframe) {
      window.merchantos.utils.openUrl(magicLink);
      return null;
    }

    return (
      <>
        <style
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `.react-page { height: 100% }`,
          }}
        />
        <StyledIframe src={`${magicLink}/${window.location.search}`} title="Accounting" />
      </>
    );
  }
}

export default withLocalizeAwareIframe(
  withRouteAwareIframe(withValidSessionIframe(AccountingPosa))
);
